import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import type { Notification } from "@/lib/types";

/**
 * Liefert die Benachrichtigungen des angemeldeten Users.
 * User-scoped (nicht workspace-scoped) — ein Bell-Icon, alle Workspaces.
 *
 * Backend: GET /api/v1/notifications
 */
export function useNotifications(opts?: { unreadOnly?: boolean; limit?: number }) {
  const params: Record<string, string> = {};
  if (opts?.unreadOnly) params.unread_only = "true";
  if (opts?.limit) params.limit = String(opts.limit);

  return useQuery({
    queryKey: ["notifications", opts ?? {}],
    queryFn: () =>
      apiClient.get<Notification[]>(`/api/v1/notifications`, {
        query: Object.keys(params).length ? params : undefined,
      }),
    staleTime: 30_000,
  });
}

/**
 * Liefert die Anzahl ungelesener Notifications für das Bell-Badge.
 * Backend: GET /api/v1/notifications/unread-count → { count: number }
 */
export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: async () => {
      const r = await apiClient.get<{ count: number }>(`/api/v1/notifications/unread-count`);
      return r?.count ?? 0;
    },
    staleTime: 30_000,
  });
}

/**
 * Markiert eine einzelne Notification als gelesen.
 * Backend: POST /api/v1/notifications/:id/read
 */
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) =>
      apiClient.post(`/api/v1/notifications/${notificationId}/read`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

/**
 * Markiert alle Notifications des Users als gelesen.
 * Backend: POST /api/v1/notifications/read-all
 */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post(`/api/v1/notifications/read-all`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}
